/**
 * Navigation config reader.
 *
 * The only place that knows which drupalSettings keys feed the
 * navigation engine. Returns a typed config, or null when
 * navigation is disabled (disabled flag, missing config, or dev
 * mode on a non-SDX-engine page).
 */

import { isAnyFrameworkInDevMode } from './dev-mode';
import { logger } from './logger';

export interface NavigateConfig {
  /** CSS selector chain for the swappable main content region. */
  contentSelector: string;
  /** Delay in ms before a hovered link starts prefetching. */
  prefetchDelay: number;
  /** Intercept non-multipart POST forms and route through the SPA engine. */
  interceptForms: boolean;
  /** Show the Drupal Batch API progress in a modal overlay. */
  batchModal: boolean;
  /** Regex source used by isNavigable() to exclude paths like /admin or /batch. */
  excludePattern: string;
  /** Prefetch navigable links as they enter the viewport. */
  viewportPrefetch: boolean;
}

const DEFAULT_CONTENT_SELECTOR = '[role="main"], main, .layout-content';
const DEFAULT_PREFETCH_DELAY = 65;
const DEFAULT_EXCLUDE_PATTERN = '^\\/(admin|user\\/\\d|node\\/\\d+\\/edit|batch)';

type RawNavigateSettings = {
  enabled?: unknown;
  selector?: unknown;
  prefetchDelay?: unknown;
  interceptForms?: unknown;
  batchModal?: unknown;
  excludePattern?: unknown;
  viewportPrefetch?: unknown;
};

/**
 * Reads the navigation config from drupalSettings and returns a
 * frozen NavigateConfig when navigation should run on this page.
 *
 * Returns null when:
 * - drupalSettings.sdx.navigate is missing or enabled is falsy
 * - any framework is in Vite dev mode AND the current page is not
 *   an SDX-engine page. HTML-swap navigation cannot survive the
 *   destruction of a Vite HMR runtime context, so the engine must
 *   disable itself to avoid subtle breakage.
 */
export function readNavigateConfig(settings: DrupalSettings): NavigateConfig | null {
  const sdx = settings.sdx as { navigate?: RawNavigateSettings } | undefined;
  const raw = sdx?.navigate;
  if (!raw || !raw.enabled) {
    return null;
  }

  if (isAnyFrameworkInDevMode(settings) && !hasSdxEnginePage()) {
    logger.info('Disabled - Vite dev server active. Navigate works in production builds.');
    return null;
  }

  const config: NavigateConfig = {
    contentSelector: stringOr(raw.selector, DEFAULT_CONTENT_SELECTOR),
    prefetchDelay: positiveIntOr(raw.prefetchDelay, DEFAULT_PREFETCH_DELAY),
    interceptForms: raw.interceptForms !== false,
    batchModal: raw.batchModal !== false,
    excludePattern: stringOr(raw.excludePattern, DEFAULT_EXCLUDE_PATTERN),
    viewportPrefetch: raw.viewportPrefetch !== false,
  };

  return Object.freeze(config);
}

/**
 * Returns true when the current document contains an SDX engine
 * mount point, which is the only environment where HTML-swap
 * navigation is unconditionally safe under Vite dev mode.
 */
function hasSdxEnginePage(): boolean {
  return !!document.querySelector('.sdx-component[data-component]');
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function positiveIntOr(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  return fallback;
}
